const API_BASE = '/api'

async function authFetch(path: string, options?: RequestInit): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, { credentials: 'include', ...options })
  if (res.status === 401) {
    window.location.href = '/auth/login'
    throw new Error('Session expired')
  }
  return res
}

async function failFriendly(res: Response, fallback: string): Promise<never> {
  let msg = fallback
  try {
    const body = await res.json()
    if (body?.error && typeof body.error === 'string') msg = body.error
  } catch {
    void 0
  }
  throw new Error(msg)
}

export interface OrgGitHubSettings {
  installationId: string | null
  repoOwner: string | null
  repoName: string | null
  repoRef: string
  connected: boolean
}

export interface OrgRepoCheck {
  checkedAt: string | null
  status: string | null
  error: string | null
  syncedCommitSha: string | null
}

export interface OrgSettings {
  id: string
  name: string
  github: OrgGitHubSettings
  repoCheck: OrgRepoCheck
  createdAt: string
  updatedAt: string
  externalId?: string | null
  connectedAt?: string | null
}

export interface OrgSecret {
  id: string
  key: string
  configured: boolean
  status: string
  keyVersion: string
  createdBy?: string | null
  updatedBy?: string | null
  createdAt: string
  updatedAt: string
}

export interface IdentityGroup {
  id: string
  externalId: string
  groupEmail?: string | null
  displayName: string
  status: string
  lastSyncedAt?: string | null
}

export interface WorkspaceGroupBinding {
  id: string
  identityGroupId: string
  externalId: string
  groupEmail?: string | null
  displayName: string
  status: string
}

export interface Workspace {
  id: string
  key: string
  rootPath: string
  displayName: string
  description?: string | null
  status: string
  lastDiscoveredCommitSha?: string | null
  lastSanityStatus?: string | null
  lastSanityMessage?: string | null
  createdAt: string
  updatedAt: string
  groupBindings?: WorkspaceGroupBinding[]
}

export interface GitHubRepository {
  owner: string
  name: string
  fullName: string
  defaultBranch: string
  private: boolean
}

export interface GitHubBranch {
  name: string
  sha: string
  protected: boolean
}

export interface UpdateOrgSettingsBody {
  name?: string
}

export async function fetchOrgSettings(): Promise<OrgSettings> {
  const res = await authFetch('/org/settings')
  if (!res.ok) await failFriendly(res, "Couldn't load organization settings.")
  const body: { organization: OrgSettings } = await res.json()
  return body.organization
}

export async function updateOrgSettings(body: UpdateOrgSettingsBody): Promise<OrgSettings> {
  const res = await authFetch('/org/settings', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't save organization settings.")
  const data: { organization: OrgSettings } = await res.json()
  return data.organization
}

export async function fetchOrgSecrets(): Promise<OrgSecret[]> {
  const res = await authFetch('/org/secrets')
  if (!res.ok) await failFriendly(res, "Couldn't load organization secrets.")
  const body: { secrets: OrgSecret[] } = await res.json()
  return body.secrets
}

export async function upsertOrgSecret(key: string, value: string): Promise<OrgSecret> {
  const res = await authFetch(`/org/secrets/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ value }),
  })
  if (!res.ok) await failFriendly(res, "Couldn't save organization secret.")
  const body: { secret: OrgSecret } = await res.json()
  return body.secret
}

export async function deleteOrgSecret(key: string): Promise<void> {
  const res = await authFetch(`/org/secrets/${encodeURIComponent(key)}`, { method: 'DELETE' })
  if (!res.ok) await failFriendly(res, "Couldn't delete organization secret.")
}

export async function fetchGitHubRepositories(): Promise<GitHubRepository[]> {
  const res = await authFetch('/org/github/repositories')
  if (!res.ok) await failFriendly(res, "Couldn't load GitHub repositories.")
  const body: { repositories: GitHubRepository[] } = await res.json()
  return body.repositories
}

export async function fetchGitHubBranches(owner: string, repo: string): Promise<GitHubBranch[]> {
  const res = await authFetch(`/org/github/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/branches`)
  if (!res.ok) await failFriendly(res, "Couldn't load GitHub branches.")
  const body: { branches: GitHubBranch[] } = await res.json()
  return body.branches
}

export async function saveGitHubRepository(body: { owner: string; name: string; ref: string }): Promise<OrgSettings> {
  const res = await authFetch('/org/github/repository', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) await failFriendly(res, "Couldn't save GitHub repository.")
  const data: { organization: OrgSettings } = await res.json()
  return data.organization
}

export async function fetchIdentityGroups(): Promise<IdentityGroup[]> {
  const res = await authFetch('/org/identity/groups')
  if (!res.ok) await failFriendly(res, "Couldn't load access groups.")
  const body: { groups: IdentityGroup[] } = await res.json()
  return body.groups
}

export async function syncIdentityGroups(): Promise<IdentityGroup[]> {
  const res = await authFetch('/org/identity/sync', { method: 'POST' })
  if (!res.ok) await failFriendly(res, "Couldn't sync access groups.")
  const body: { groups: IdentityGroup[] } = await res.json()
  return body.groups
}

export async function fetchOrgWorkspaces(): Promise<Workspace[]> {
  const res = await authFetch('/org/workspaces')
  if (!res.ok) await failFriendly(res, "Couldn't load workspaces.")
  const body: { workspaces: Workspace[] } = await res.json()
  return body.workspaces
}

export async function discoverOrgWorkspaces(): Promise<Workspace[]> {
  const res = await authFetch('/org/workspaces/discover', { method: 'POST' })
  if (!res.ok) await failFriendly(res, "Couldn't discover workspaces.")
  const body: { workspaces: Workspace[] } = await res.json()
  return body.workspaces
}

export async function bindWorkspaceGroup(workspaceKey: string, groupId: string): Promise<void> {
  const res = await authFetch(`/org/workspaces/${encodeURIComponent(workspaceKey)}/groups/${encodeURIComponent(groupId)}`, {
    method: 'PUT',
  })
  if (!res.ok) await failFriendly(res, "Couldn't bind workspace group.")
}

export async function unbindWorkspaceGroup(workspaceKey: string, groupId: string): Promise<void> {
  const res = await authFetch(`/org/workspaces/${encodeURIComponent(workspaceKey)}/groups/${encodeURIComponent(groupId)}`, {
    method: 'DELETE',
  })
  if (!res.ok) await failFriendly(res, "Couldn't unbind workspace group.")
}
